import { Exception, IArgumentType } from '@ch-types/Exception';

/**
 * This can be used in conjunction with the SeawardCrossFrameObjectTranslator
 * to make sure that Exception can be serialized/deserialized as they go across frames.\
 *
 * Base Exception for all Seaward Exceptions.
 */
export class SeawardException extends Exception {
    protected model = 'SeawardException';

    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): SeawardException {
        return new SeawardException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when the current environment (Android, iOS, OSX, etc.)
 * doesn't support the requested action (Example: Bluetooth).
 */
export class UnsupportedEnvironmentException extends SeawardException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'UnsupportedEnvironmentException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): UnsupportedEnvironmentException {
        return new UnsupportedEnvironmentException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when the request can't be fulfilled because it's in an illegal state.
 */
export class IllegalStateException extends SeawardException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'IllegalStateException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): IllegalStateException {
        return new IllegalStateException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when Seaward doesn't support the action requested for the specific listener.
 */
export class UnsupportedActionException extends SeawardException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'UnsupportedActionException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): UnsupportedActionException {
        return new UnsupportedActionException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when the arguments are not valid.
 */
export class InvalidArgumentsException extends SeawardException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'InvalidArgumentsException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): InvalidArgumentsException {
        return new InvalidArgumentsException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when there is a network error.
 */
export class NetworkException extends SeawardException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'NetworkException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): NetworkException {
        return new NetworkException({
            message: obj.message,
        });
    }
}
